import type { SuperHubHashRouteParams, SuperHubRouteParams } from '@routes/SuperHub/types';
import type { ReactNode } from 'react';

import { useEffect, useMemo, useRef } from 'react';
import { matchPath, useLocation, useHistory } from 'react-router-dom';

import { SuperHubHashRoutePaths, SuperHubRoutePaths } from '@routes/SuperHub/types';

import { superHubStore, useSuperHubStore } from '.';

/**
 * Routes that need the full admin shell (sidebar, hash overlays, editor). When the user
 * navigates here while "preview" has hidden the admin UI, we show the shell again so the
 * URL and UI stay in sync (e.g. browser back from preview to an edit or settings URL).
 */
function routeRequiresAdminShell(pathname: string, hash: string): boolean {
  const pathMatch = matchPath<SuperHubRouteParams>(pathname, Object.values(SuperHubRoutePaths));
  const action = pathMatch?.params.action ?? null;
  if (action && ['compare', 'create', 'review', 'update'].includes(action)) {
    return true;
  }

  const hashPath = hash?.startsWith('#') ? hash.slice(1) : hash;
  if (!hashPath || hashPath === '/' || hashPath === '') {
    return false;
  }

  return !!matchPath<SuperHubHashRouteParams>(hashPath, Object.values(SuperHubHashRoutePaths));
}

export interface ConnectSuperHubStoreToRouterProps {
  children: ReactNode;
  /** Whether to disable hash route syncing and only use path routes. */
  disableHashRoutes?: boolean;
}

/**
 * List of hash `action` params that should *not* be preserved when location is
 * being updated.
 */
const excludedHashActions: SuperHubHashRouteParams['action'][] = [
  'branches',
  'docs-audit',
  'my-developers',
  'settings',
];

function ConnectPathOnly({ children }: ConnectSuperHubStoreToRouterProps) {
  const updateRoute = useSuperHubStore(s => s.updateRoute);
  const history = useHistory();
  const { pathname, hash } = useLocation();

  /** Whether the location hash matches a known SuperHub hash route */
  const isHashRoute = useMemo(() => {
    // Include the root hash as a valid route so we can clean it up.
    if (hash === '#/') return true;

    const hashPath = hash.substring(1);
    return !!matchPath<SuperHubHashRouteParams>(hashPath, Object.values(SuperHubHashRoutePaths));
  }, [hash]);

  // Update our store state on every route change.
  useEffect(() => {
    updateRoute(pathname);

    const { isAdminUIHidden, toggleAdminUIHidden, setPreviousPreviewEntry } = superHubStore.getState();
    if (isAdminUIHidden && routeRequiresAdminShell(pathname, hash)) {
      toggleAdminUIHidden(false, { restorePreviousHash: false });
      setPreviousPreviewEntry(null);
    }
  }, [hash, pathname, updateRoute]);

  // Clean up the location hash since we are in path-only mode, but only if we
  // are currently on a known hash route. This keeps things tidy and prevents
  // stale hash paths from persisting in the URL.
  // `/review/docs/page#/branches` -> `/review/docs/page`
  useEffect(() => {
    if (!isHashRoute) return;

    history.replace({ pathname, hash: '' });
  }, [history, isHashRoute, pathname]);

  return <>{children}</>;
}

function ConnectPathHash({ children }: ConnectSuperHubStoreToRouterProps) {
  const [updateRoute, isEditing] = useSuperHubStore(s => [s.updateRoute, s.isEditing]);
  const history = useHistory();
  const { pathname, hash } = useLocation();

  /** Maintains a record of the last location hash navigated to. */
  const lastHashRef = useRef<string>(hash);

  /** Whether we have forced the user into a temporary view mode after entering an appearance route. */
  const isInTemporaryViewModeRef = useRef<boolean>(false);

  // Responsible for temporarily switching to View mode when navigating
  // Appearance settings and then restoring them back to Edit mode when exiting.
  // TODO: Consider moving this logic out to a separate hook that only runs when
  // users are inside an Edit route.
  useEffect(() => {
    const match = matchPath<SuperHubRouteParams>(pathname, Object.values(SuperHubRoutePaths));
    const hashMatch = matchPath<SuperHubHashRouteParams>(hash.substring(1), Object.values(SuperHubHashRoutePaths));
    const {
      params: { section = null, slug = null },
    } = match ?? { params: {} };
    const {
      params: { action: hashAction },
    } = hashMatch ?? { params: {} };

    /**
     * If we are entering an appearance route and the user is currently in edit mode, we should temporarily
     * redirect them to the view mode of the current section. It isn't very useful to change the appearance
     * settings while in edit mode -- the user won't see the changes live updating,
     */
    const isEnteringAppearance = hash && hashAction === 'appearance';

    if (isEditing && isEnteringAppearance && !isInTemporaryViewModeRef.current) {
      history.replace({
        pathname: `/${[section, slug].filter(Boolean).join('/')}`,
        hash,
      });
      isInTemporaryViewModeRef.current = true;
      return;
    }

    /**
     * If the user exits a hash route with a temporary view mode state, attempt to return them to edit mode.
     */
    const isExitHash = hash !== '' && !hashAction;
    if (isExitHash && isInTemporaryViewModeRef.current) {
      /** Not all sections have all of the 'create', 'update', 'compare' edit mode route
       * actions and a user can navigate to different sections while in this temporary view mode. So we just
       * redirect them to the common 'update' route action to avoid a bad redirect. */
      const redirectUrl = `/${['update', section, slug].filter(Boolean).join('/')}`;

      /** Ensure the redirect is a valid edit mode route before redirecting. */
      const redirectMatch = matchPath<SuperHubRouteParams>(redirectUrl, SuperHubRoutePaths.editPage);
      if (redirectMatch) {
        history.replace(redirectUrl);
        isInTemporaryViewModeRef.current = false;
        lastHashRef.current = '';
        return;
      }

      isInTemporaryViewModeRef.current = false;
    }
  }, [hash, history, isEditing, pathname]);

  // Responsible for updating our store on every route change.
  useEffect(() => {
    /**
     * Preserve the HashRouter state when navigating to a new location without a
     * hash. This is useful when the user is navigating to a new page in the Hub
     * while a hash route driven UI is visible, like Appearance settings.
     */
    const toHashlessRoute = !hash;
    const previousHashMatch = toHashlessRoute
      ? matchPath<SuperHubHashRouteParams>(lastHashRef.current.substring(1), Object.values(SuperHubHashRoutePaths))
      : null;
    /**
     * Re-apply the overlay hash when the user navigates to a pathname that omits
     * it (e.g. clicking a doc link while Appearance is open). Do **not** do this
     * on browser back/forward (`POP`): the user intentionally moved to a prior
     * history entry, and restoring the hash would cancel that navigation.
     */
    const preserveHash =
      history.action !== 'POP' && !!previousHashMatch && !excludedHashActions.includes(previousHashMatch.params.action);

    if (preserveHash) {
      history.replace({
        pathname,
        hash: lastHashRef.current,
      });
    } else {
      updateRoute(pathname, hash);

      const { isAdminUIHidden, toggleAdminUIHidden, setPreviousPreviewEntry } = superHubStore.getState();
      if (isAdminUIHidden && routeRequiresAdminShell(pathname, hash)) {
        toggleAdminUIHidden(false, { restorePreviousHash: false });
        setPreviousPreviewEntry(null);
      }
    }

    // Keep record of last updated hash.
    lastHashRef.current = hash;
  }, [hash, history, pathname, updateRoute]);

  return <>{children}</>;
}

/**
 * Middleware component listens for Hub browser router updates and continually
 * updates the store state whenever the route changes.
 */
export function ConnectSuperHubStoreToRouter(props: ConnectSuperHubStoreToRouterProps) {
  if (props.disableHashRoutes) {
    return <ConnectPathOnly {...props} />;
  }

  return <ConnectPathHash {...props} />;
}
